import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { config } from '../config/environment';
import errorHandlingService from './errorHandlingService';
import { ERROR_CODES } from '../types/errors';
import refreshTokenManager from './refreshTokenManager';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
    code: string;
    details?: any[];
  };
}

export interface ApiRequestConfig extends AxiosRequestConfig {
  context?: string;
  component?: string;
  retryAttempts?: number;
  retryDelay?: number;
}

export class EnhancedApiClient {
  private client: AxiosInstance;
  private defaultRetryAttempts: number = 3;
  private defaultRetryDelay: number = 2000; // Increased from 1000ms to 2000ms

  constructor() {
    this.client = axios.create({
      baseURL: config.API_URL,
      timeout: config.REQUEST_TIMEOUT,
      withCredentials: config.USE_COOKIES,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor - Main app, supervisor, and worker tokens
    this.client.interceptors.request.use(
      (config) => {
        // Check for main app token first
        const mainToken = localStorage.getItem("token");
        if (mainToken) {
          config.headers.Authorization = `Bearer ${mainToken}`;
        } else {
          // Check for supervisor token
          const supervisorToken = localStorage.getItem("supervisorToken");
          if (supervisorToken) {
            config.headers.Authorization = `Bearer ${supervisorToken}`;
          } else {
            // Check for worker token
            const workerToken = localStorage.getItem("workerToken");
            if (workerToken) {
              config.headers.Authorization = `Bearer ${workerToken}`;
            }
          }
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - Main app and worker tokens
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        // Handle CORS errors specifically
        if (error.code === 'ERR_NETWORK' || error.response?.status === 0) {
          console.warn('CORS or Network error detected:', error.message);
          // Add a small delay before retrying CORS errors
          await this.delay(1000);
        }

        // Handle token refresh for 401 errors - Main app, supervisor, and worker
        if (error.response?.status === 401 && !originalRequest._retry) {
          // Check if this is a supervisor request
          const supervisorToken = localStorage.getItem("supervisorToken");
          if (supervisorToken) {
            // For supervisor requests, just reject - supervisors don't have refresh tokens
            return Promise.reject(error);
          }
          // Check if this is a worker request
          const workerToken = localStorage.getItem("workerToken");
          if (workerToken) {
            // Check error code to provide better error message
            const errorData = (error.response?.data as any)?.error;
            const errorCode = errorData?.code;
            const errorMessage = errorData?.message || 'Authentication failed';
            
            console.error('Worker authentication error:', {
              code: errorCode,
              message: errorMessage,
              fullError: errorData
            });
            
            // For worker requests, clear auth and redirect to login
            localStorage.removeItem("worker");
            localStorage.removeItem("workerToken");
            localStorage.removeItem("workerLoginTime");
            
            // Show user-friendly error message for specific error codes
            if (errorCode === 'NOT_A_WORKER' || errorCode === 'WORKER_INACTIVE' || errorCode === 'WORKER_NOT_FOUND' || errorCode === 'USER_NOT_FOUND' || errorCode === 'TOKEN_EXPIRED' || errorCode === 'WORKER_TOKEN_EXPIRED') {
              console.error(`Worker auth failed: ${errorMessage}. Clearing session and redirecting to login.`);
              
              // Use errorHandlingService to show toast notification
              errorHandlingService.handleApiError(error, 'Worker Authentication');
              
              // Redirect to worker login page after a short delay to allow toast to show
              setTimeout(() => {
                window.location.href = '/infinity-app';
              }, 1500);
            }
            
            // For worker requests, just reject - workers don't have refresh tokens
            return Promise.reject(error);
          } else {
            // Main app - try to refresh token
            return this.handleTokenRefresh(originalRequest);
          }
        }

        // Handle 403 errors specifically
        if (error.response?.status === 403) {
          const errorData = error.response.data as any;
          if (errorData?.error?.code === ERROR_CODES.NO_ROLES) {
            // This is handled by the error handling service
            // but we can add specific logic here if needed
            console.log('User has no roles - triggering role assignment flow');
          } else if (errorData?.error?.code === 'WORKER_ACCESS_DENIED') {
            // Handle worker trying to access main app
            console.log('Worker access denied - redirecting to worker portal');
            // Clear any existing auth data
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            // Redirect to worker login
            window.location.href = '/infinity-app';
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private async handleTokenRefresh(originalRequest: any): Promise<AxiosResponse> {
    originalRequest._retry = true;

    try {
      // Use centralized refresh manager to prevent multiple simultaneous refresh calls
      const newToken = await refreshTokenManager.refreshToken();
      
      if (newToken) {
        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return this.client(originalRequest);
      }
    } catch (refreshError) {
      console.error('Token refresh failed:', refreshError);
      // The refresh manager already handles cleanup and redirect
    }

    return Promise.reject(new Error('Token refresh failed'));
  }

  private async executeWithRetry<T>(
    requestFn: () => Promise<AxiosResponse<T>>,
    config: ApiRequestConfig
  ): Promise<ApiResponse<T>> {
    const retryAttempts = config.retryAttempts || this.defaultRetryAttempts;
    const retryDelay = config.retryDelay || this.defaultRetryDelay;
    let lastError: any;

    for (let attempt = 0; attempt <= retryAttempts; attempt++) {
      try {
        const response = await requestFn();
        return response.data as ApiResponse<T>;
      } catch (error: any) {
        lastError = error;
        
        // Don't retry certain error types
        if (this.shouldNotRetry(error)) {
          break;
        }

        // If this is the last attempt, don't wait
        if (attempt === retryAttempts) {
          break;
        }

        // Wait before retrying - use linear backoff for timeout errors
        const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
        const delay = isTimeout ? retryDelay * (attempt + 1) : retryDelay * Math.pow(2, attempt);
        await this.delay(delay);
      }
    }

    // All retries failed, handle the error
    const processedError = await errorHandlingService.handleApiError(
      lastError,
      config.context || 'API Request',
      config.component
    );

    // Return error response
    return {
      success: false,
      error: {
        message: processedError.userMessage,
        code: processedError.code,
        details: processedError.originalError?.response?.data?.error?.details,
      },
    };
  }

  private shouldNotRetry(error: any): boolean {
    // Don't retry client errors (4xx) except for network issues and CORS
    if (error.response?.status >= 400 && error.response?.status < 500) {
      // Retry CORS errors (status 0) and network errors
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CANCELED' || error.response?.status === 0) {
        return false;
      }
      return true;
    }
    
    // Don't retry if explicitly cancelled
    if (axios.isCancel(error)) {
      return true;
    }

    // Retry CORS, network errors, and timeout errors
    if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CANCELED' || error.code === 'ECONNABORTED') {
      return false;
    }

    return false;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Generic request methods
  async get<T = any>(url: string, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
    return this.executeWithRetry(
      () => this.client.get<T>(url, { ...config, timeout: config?.timeout || this.client.defaults.timeout }),
      config || {}
    );
  }

  async post<T = any>(url: string, data?: any, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
    return this.executeWithRetry(
      () => this.client.post<T>(url, data, { ...config, timeout: config?.timeout || this.client.defaults.timeout }),
      config || {}
    );
  }

  async put<T = any>(url: string, data?: any, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
    return this.executeWithRetry(
      () => this.client.put<T>(url, data, { ...config, timeout: config?.timeout || this.client.defaults.timeout }),
      config || {}
    );
  }

  async patch<T = any>(url: string, data?: any, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
    return this.executeWithRetry(
      () => this.client.patch<T>(url, data, { ...config, timeout: config?.timeout || this.client.defaults.timeout }),
      config || {}
    );
  }

  async delete<T = any>(url: string, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
    return this.executeWithRetry(
      () => this.client.delete<T>(url, { ...config, timeout: config?.timeout || this.client.defaults.timeout }),
      config || {}
    );
  }

  // Utility method to check if response is successful
  static isSuccessResponse<T>(response: ApiResponse<T>): response is { success: true; data: T } {
    return response.success === true && response.data !== undefined;
  }

  // Utility method to check if response is an error
  static isErrorResponse<T>(response: ApiResponse<T>): response is { success: false; error: any } {
    return response.success === false && response.error !== undefined;
  }

  // Method to handle specific error codes
  static handleSpecificError<T>(response: ApiResponse<T>, onError?: (error: any) => void): T | null {
    if (EnhancedApiClient.isSuccessResponse(response)) {
      return response.data;
    }

    if (onError) {
      onError(response.error);
    }

    return null;
  }

  // Get base URL for debugging
  getBaseURL(): string {
    return this.client.defaults.baseURL || '';
  }
}

// Create and export singleton instance
export const enhancedApiClient = new EnhancedApiClient();

// Export utility functions
export const { isSuccessResponse, isErrorResponse, handleSpecificError } = EnhancedApiClient;

export default enhancedApiClient; 